import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { getComplianceDashboard, getIdentityMatchDashboard, getRoutingDashboard, getJurisdictions, getOffensesBySearchId, getBusinessMetrics, getQaErrorRate, getSearchDashboard, getCasesBySearchId, getEvaluationsBySearch, getDecisionHistory, getRunComparison, getModelVersions, getVersionComparison, getRunHistory, getMeteringDashboard, getMeteringOffenses, getTelemetryDashboard, updateQaDecision } from '@/services/analytics.api'
import { ComplianceDashboardQuery, IdentityMatchDashboardQuery, RoutingDashboardQuery, RoutingDashboardResponse, SearchDashboardQuery, MeteringDashboardQuery, TelemetryDashboardQuery } from '@/types'
import { usePermissions } from './usePermissions'

function useTenantQueryKeyPart() {
  const { effectiveTenantId } = usePermissions()
  return effectiveTenantId ?? 'portfolio'
}

export const useComplianceDashboard = (filter: ComplianceDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['compliance-dashboard', tenantKey, filter],
    queryFn: () => getComplianceDashboard(filter),
    enabled: !!filter.startDate && !!filter.endDate,
  })
}

export const useIdentityMatchDashboard = (filter: IdentityMatchDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['identity-match-dashboard', tenantKey, filter],
    queryFn: () => getIdentityMatchDashboard(filter),
    enabled: !!filter.startDate && !!filter.endDate,
  })
}

/**
 * Fetches only metrics (summary, heatmap, breakdowns). Keyed by date range only,
 * so pagination/filter changes don't trigger a metrics re-fetch.
 */
export const useRoutingMetrics = (filter: RoutingDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['routing-metrics', tenantKey, filter.startDate, filter.endDate, filter.searchType, filter.sourceNorm, filter.modelVersion],
    queryFn: () => getRoutingDashboard({
      startDate: filter.startDate,
      endDate: filter.endDate,
      page: 1,
      pageSize: 1,
      searchType: filter.searchType,
      sourceNorm: filter.sourceNorm,
      modelVersion: filter.modelVersion,
      includeMetrics: true,
    }),
    enabled: !!filter.startDate && !!filter.endDate,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetches paginated table rows only (no metrics). Uses keepPreviousData so the table
 * stays visible while the next page loads.
 */
export const useRoutingRows = (filter: RoutingDashboardQuery & { enabled?: boolean }) => {
  const tenantKey = useTenantQueryKeyPart()
  const { enabled = true, ...queryFilter } = filter
  return useQuery({
    queryKey: ['routing-rows', tenantKey, queryFilter],
    queryFn: () => getRoutingDashboard({ ...queryFilter, includeMetrics: false }),
    enabled: enabled && !!filter.startDate && !!filter.endDate,
    placeholderData: keepPreviousData,
  })
}

/** @deprecated Use useRoutingMetrics + useRoutingRows instead */
export const useRoutingDashboard = (filter: RoutingDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['routing-dashboard', tenantKey, filter],
    queryFn: () => getRoutingDashboard(filter),
    enabled: !!filter.startDate && !!filter.endDate,
  })
}

export const useJurisdictions = () => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['jurisdictions', tenantKey],
    queryFn: getJurisdictions,
  })
}

// Hook to fetch all offenses for a given search_id from the database
export const useOffensesBySearchId = (searchId: string | null, modelVersion?: string | null) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['offenses-by-search', tenantKey, searchId, modelVersion],
    queryFn: () => getOffensesBySearchId(searchId!, modelVersion || undefined),
    enabled: !!searchId,
  })
}

// Hook to fetch business metrics for SNH AI dashboard
export const useBusinessMetrics = (startDate: string, endDate: string) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['business-metrics', tenantKey, startDate, endDate],
    queryFn: () => getBusinessMetrics(startDate, endDate),
    enabled: !!startDate && !!endDate,
  })
}

export const useQaErrorRate = (days: number, page: number, pageSize: number) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['qa-error-rate', tenantKey, days, page, pageSize],
    queryFn: () => getQaErrorRate(days, page, pageSize),
    placeholderData: keepPreviousData,
  })
}

export const useSearchDashboard = (filter: SearchDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['search-dashboard', tenantKey, filter],
    queryFn: () => getSearchDashboard(filter),
    enabled: !!filter.startDate && !!filter.endDate,
    placeholderData: keepPreviousData,
  })
}

export const useCasesBySearchId = (searchId: string | null) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['cases-by-search', tenantKey, searchId],
    queryFn: () => getCasesBySearchId(searchId!),
    enabled: !!searchId,
  })
}

export const useEvaluationsBySearch = (searchId: string | null) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['evaluations-by-search', tenantKey, searchId],
    queryFn: () => getEvaluationsBySearch(searchId!),
    enabled: !!searchId,
    staleTime: 5 * 60 * 1000,
  })
}

export const useDecisionHistory = (searchId: string | null) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['decision-history', tenantKey, searchId],
    queryFn: () => getDecisionHistory(searchId!),
    enabled: !!searchId,
    staleTime: 5 * 60 * 1000,
  })
}

export const useRunComparison = (startDate: string, endDate: string, searchType?: string[], sourceNorm?: string[], enabled = false) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['run-comparison', tenantKey, startDate, endDate, searchType, sourceNorm],
    queryFn: () => getRunComparison(startDate, endDate, searchType, sourceNorm),
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 10 * 60 * 1000,
  })
}

export const useModelVersions = () => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['model-versions', tenantKey],
    queryFn: () => getModelVersions(),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
}

export const useVersionComparison = (
  params: {
    startDate: string
    endDate: string
    searchType?: string[]
    sourceNorm?: string[]
    v1?: string
    v2?: string
    mode?: 'version' | 'chronological'
  },
  enabled = false,
) => {
  const tenantKey = useTenantQueryKeyPart()
  const isVersionMode = params.mode === 'version' || (!!params.v1 && !!params.v2 && params.mode !== 'chronological')
  return useQuery({
    queryKey: ['version-comparison', tenantKey, params.startDate, params.endDate, params.searchType, params.sourceNorm, params.v1, params.v2, params.mode],
    queryFn: () => getVersionComparison({
      startDate: params.startDate,
      endDate: params.endDate,
      searchType: params.searchType,
      sourceNorm: params.sourceNorm,
      v1: isVersionMode ? params.v1 : undefined,
      v2: isVersionMode ? params.v2 : undefined,
      mode: isVersionMode ? undefined : 'chronological',
    }),
    enabled: enabled && !!params.startDate && !!params.endDate && (isVersionMode ? !!params.v1 && !!params.v2 : true),
    staleTime: 10 * 60 * 1000,
  })
}

export const useRunHistory = (startDate: string, endDate: string, modelVersion: string | null, enabled = false) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['run-history', tenantKey, startDate, endDate, modelVersion],
    queryFn: () => getRunHistory(startDate, endDate, modelVersion!),
    enabled: enabled && !!startDate && !!endDate && !!modelVersion,
    staleTime: 5 * 60 * 1000,
  })
}

export const useUpdateQaDecision = () => {
  const queryClient = useQueryClient()
  const tenantKey = useTenantQueryKeyPart()

  return useMutation({
    mutationFn: ({ offenseId, searchId, qaDecision }: { offenseId: string; searchId: string; qaDecision: string }) =>
      updateQaDecision(offenseId, searchId, qaDecision),
    onMutate: async ({ offenseId, qaDecision }) => {
      await queryClient.cancelQueries({ queryKey: ['routing-rows', tenantKey], exact: false })

      const queryCache = queryClient.getQueryCache()
      const matchingQueries = queryCache.findAll({ queryKey: ['routing-rows', tenantKey] })

      const previousData: Array<{ queryKey: readonly unknown[]; data: unknown }> = []
      for (const query of matchingQueries) {
        previousData.push({ queryKey: query.queryKey, data: query.state.data })
        queryClient.setQueryData<RoutingDashboardResponse>(query.queryKey, (old) => {
          if (!old) return old
          return {
            ...old,
            rows: old.rows.map((row) =>
              row.offense_id === offenseId ? { ...row, qa_decision: qaDecision } : row,
            ),
          }
        })
      }
      return { previousData }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        for (const { queryKey, data } of context.previousData) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['routing-rows', tenantKey], exact: false })
    },
  })
}

export const useMeteringDashboard = (filter: MeteringDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['metering-dashboard', tenantKey, filter],
    queryFn: () => getMeteringDashboard(filter),
    enabled: !!filter.startDate && !!filter.endDate,
  })
}

export const useTelemetryDashboard = (filter: TelemetryDashboardQuery) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['telemetry-dashboard', tenantKey, filter],
    queryFn: () => getTelemetryDashboard(filter),
    enabled: !!filter.startDate && !!filter.endDate,
  })
}

export const useMeteringOffenses = (searchId: string | null, modelVersion?: string) => {
  const tenantKey = useTenantQueryKeyPart()
  return useQuery({
    queryKey: ['metering-offenses', tenantKey, searchId, modelVersion],
    queryFn: () => getMeteringOffenses(searchId!, modelVersion),
    enabled: !!searchId,
  })
}
